import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RegistrationStepRoute } from '@app/features/registration/constants/registration-steps.const';

import { RegistrationNavigationService } from './registration-navigation.service';
import { RegistrationPreviewPresenter } from './registration-preview.presenter';
import { RegistrationProgressPresenter } from './registration-progress.presenter';
import { RegistrationStepRegistry } from './registration-step-registry.service';
import { RegistrationStore } from './registration-store.service';
import { RegistrationWizardService } from './registration-wizard.service';
import {
  CompanyInformationInquiryDto,
  ContactInformationAddressInquiryDto,
  PersonalInformationInquiryDto,
  RegistrationField,
  RegistrationFieldId,
  RegistrationResult,
  RegistrationSection,
  RegistrationSectionStatus,
  RegistrationStepForm,
  SubmitBasicInformationDto,
  SubmitCompanyInformationDto,
  SubmitContactInformationDto,
  SubmitPersonalInformationDto,
  UpdateBasicInformationDto,
  UpdateBasicInformationIsLegalDto,
  UpdateCompanyInformationDto,
  UpdateContactInformationDto,
  UpdatePersonalInformationDto,
} from '../types';

/**
 * Thin facade that composes the registration store, navigation, progress
 * presenter, wizard and step registry behind a single surface for the
 * registration feature's components and guards. It holds no logic of its own;
 * every member delegates to the owning collaborator.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationFacade {
  private readonly store = inject(RegistrationStore);
  private readonly navigation = inject(RegistrationNavigationService);
  private readonly progress = inject(RegistrationProgressPresenter);
  private readonly preview = inject(RegistrationPreviewPresenter);
  private readonly stepRegistry = inject(RegistrationStepRegistry);
  private readonly wizard = inject(RegistrationWizardService);

  // State
  readonly registration = this.store.registration;
  readonly loading = this.store.loading;
  readonly savingCurrentStep = this.stepRegistry.savingCurrentStep;
  readonly submittingCurrentStep = this.stepRegistry.submittingCurrentStep;
  readonly submitting = this.store.submitting;
  readonly isVerified = this.store.isVerified;
  readonly status = this.store.status;
  readonly sections = this.store.sections;

  // Wizard
  readonly stepRoutes = this.wizard.stepRoutes;
  readonly isLegalEntity = this.wizard.isLegalEntity;

  // Navigation
  readonly isPendingRoute = this.navigation.isPendingRoute;
  readonly wizardStepIndex = this.navigation.wizardStepIndex;
  readonly isPreviousDisabled = this.navigation.isPreviousDisabled;
  readonly isNextDisabled = this.navigation.isNextDisabled;

  // Progress / view models
  readonly progressSteps = this.progress.progressSteps;
  readonly previewSections = this.preview.previewSections;
  readonly stepsContainerWidthClass = this.progress.stepsContainerWidthClass;
  readonly showReviewPanel = this.progress.showReviewPanel;
  readonly showSingleIdentityStep = this.progress.showSingleIdentityStep;

  ensureLoaded(force = false): Observable<boolean> {
    return this.store.ensureLoaded(force);
  }

  refresh(): Observable<boolean> {
    return this.store.refresh();
  }

  applyRegistrationResult(result: RegistrationResult): void {
    this.store.applyRegistrationResult(result);
  }

  clear(): void {
    this.store.clear();
    this.stepRegistry.register(null);
  }

  setIsLegalEntity(value: boolean): void {
    this.wizard.setIsLegalEntity(value);
  }

  registerStep(stepForm: RegistrationStepForm | null): void {
    this.stepRegistry.register(stepForm);
  }

  submitCurrentStep(): Observable<boolean> {
    return this.stepRegistry.submitCurrentStep();
  }

  saveCurrentStep(): Observable<boolean> {
    return this.stepRegistry.saveCurrentStep();
  }

  updateBasicInformation(dto: UpdateBasicInformationDto): Observable<boolean> {
    return this.store.updateBasicInformation(dto);
  }

  updateBasicInformationIsLegal(dto: UpdateBasicInformationIsLegalDto): Observable<boolean> {
    return this.store.updateBasicInformationIsLegal(dto);
  }

  submitBasicInformation(dto: SubmitBasicInformationDto): Observable<boolean> {
    return this.store.submitBasicInformation(dto);
  }

  submitPersonalInformation(dto: SubmitPersonalInformationDto): Observable<boolean> {
    return this.store.submitPersonalInformation(dto);
  }

  inquiryPersonalInformation(dto: PersonalInformationInquiryDto): Observable<boolean> {
    return this.store.inquiryPersonalInformation(dto);
  }

  updatePersonalInformation(dto: UpdatePersonalInformationDto): Observable<boolean> {
    return this.store.updatePersonalInformation(dto);
  }

  submitCompanyInformation(dto: SubmitCompanyInformationDto): Observable<boolean> {
    return this.store.submitCompanyInformation(dto);
  }

  inquiryCompanyInformation(dto: CompanyInformationInquiryDto): Observable<boolean> {
    return this.store.inquiryCompanyInformation(dto);
  }

  updateCompanyInformation(dto: UpdateCompanyInformationDto): Observable<boolean> {
    return this.store.updateCompanyInformation(dto);
  }

  updateContactInformation(dto: UpdateContactInformationDto): Observable<boolean> {
    return this.store.updateContactInformation(dto);
  }

  submitContactInformation(dto: SubmitContactInformationDto): Observable<boolean> {
    return this.store.submitContactInformation(dto);
  }

  inquiryContactInformationAddress(dto: ContactInformationAddressInquiryDto): Observable<boolean> {
    return this.store.inquiryContactInformationAddress(dto);
  }

  isValidStepRoute(route: RegistrationStepRoute): boolean {
    return this.wizard.isValidStepRoute(route);
  }

  getField(fieldId: RegistrationFieldId): RegistrationField | undefined {
    return this.store.getField(fieldId);
  }

  getFieldValue(fieldId: RegistrationFieldId): string | null {
    return this.store.getFieldValue(fieldId);
  }

  getFieldRejectDescription(fieldId: RegistrationFieldId): string | null {
    return this.store.getFieldRejectDescription(fieldId);
  }

  isFieldEditable(fieldId: RegistrationFieldId): boolean {
    return this.store.isFieldEditable(fieldId);
  }

  isFieldVerified(fieldId: RegistrationFieldId): boolean {
    return this.store.isFieldVerified(fieldId);
  }

  isFieldControlDisabled(fieldId: RegistrationFieldId): boolean {
    return this.store.isFieldControlDisabled(fieldId);
  }

  getSection(step: number): RegistrationSection | undefined {
    return this.store.getSection(step);
  }

  getSectionByRoute(route: RegistrationStepRoute): RegistrationSection | undefined {
    return this.store.getSectionByRoute(route);
  }

  isSectionNavigable(status: RegistrationSectionStatus): boolean {
    return this.store.isSectionNavigable(status);
  }

  isSectionFinished(route: RegistrationStepRoute): boolean {
    return this.store.isSectionFinished(route);
  }

  isPendingByAdmin(): boolean {
    return this.store.isPendingByAdmin();
  }

  isFinished(): boolean {
    return this.store.isFinished();
  }

  getCurrentStepRoute(url: string): RegistrationStepRoute | null {
    return this.navigation.getCurrentStepRoute(url);
  }

  getTargetRoute(): RegistrationStepRoute | 'pending' | null {
    return this.navigation.getTargetRoute();
  }

  getNextRouteAfter(currentRoute: RegistrationStepRoute): RegistrationStepRoute | 'pending' | null {
    return this.navigation.getNextRouteAfter(currentRoute);
  }

  syncRouteWithRegistrationState(): void {
    this.navigation.syncRouteWithRegistrationState();
  }

  goToStep(route: RegistrationStepRoute): void {
    this.navigation.goToStep(route);
  }

  goPrevious(): void {
    this.navigation.goPrevious();
  }

  goNext(): void {
    this.navigation.goNext();
  }

  saveAndExit(): void {
    this.navigation.saveAndExit();
  }
}
